import { useState } from "react";
import { api } from "@/lib/api";
import { X, Mail, Loader2, CheckCircle2, FileText } from "lucide-react";

/**
 * "Send me the pitch deck" lead-capture modal.
 * Public — hits POST /pitch/request-deck which logs the lead and emails the
 * personalised PPTX in the background. Used on the landing page (source
 * "landing") and inside demo tenants via PitchDeckFab (source "demo").
 */
export default function PitchDeckRequestModal({ open, onClose, source = "landing" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dealership, setDealership] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/pitch/request-deck", {
        name: name.trim(),
        email: email.trim(),
        dealership_name: dealership.trim() || null,
        source,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "mt-1 w-full px-3 py-2 rounded-md bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="pitch-request-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md card-default p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
          data-testid="pitch-request-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="text-center py-6" data-testid="pitch-request-success">
            <CheckCircle2 className="w-12 h-12 text-success mx-auto" />
            <h3 className="font-display text-xl font-bold mt-4">Deck on its way</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Check your inbox in the next couple of minutes — and the spam folder, just in case.
              Steve will follow up personally.
            </p>
            <button type="button" onClick={onClose} className="btn-primary mt-6" data-testid="pitch-request-done-btn">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight">Get the pitch deck</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  The full ServiceSell PRO walkthrough — straight to your inbox.
                </p>
              </div>
            </div>

            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Your name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Damian Hundley-Appleton"
                  className={inputCls}
                  data-testid="pitch-request-name"
                  maxLength={80}
                  required
                  disabled={busy}
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Work email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. you@dealership.co.uk"
                  className={inputCls}
                  data-testid="pitch-request-email"
                  required
                  disabled={busy}
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  Dealership <span className="normal-case font-normal text-muted-foreground/60">(optional — personalises the deck)</span>
                </label>
                <input
                  type="text"
                  value={dealership}
                  onChange={(e) => setDealership(e.target.value)}
                  placeholder="e.g. H W Moon Toyota"
                  className={inputCls}
                  data-testid="pitch-request-dealership"
                  maxLength={120}
                  disabled={busy}
                />
              </div>

              {error && (
                <div className="text-xs text-destructive" data-testid="pitch-request-error">{error}</div>
              )}

              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="pitch-request-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {busy ? "Sending…" : "Email me the deck"}
              </button>
              <p className="text-[10px] text-muted-foreground/70 text-center leading-relaxed">
                No spam, no mailing list — just the deck and a personal follow-up from the founder.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
